import { useEffect, useState } from 'react';
import { AlertCircle, Loader2, Plus, Star, Trash2 } from 'lucide-react';
import { apiFetch } from '../lib/api';
import FiscalProfileDialog, { type FiscalProfile } from './FiscalProfileDialog';
import { useTranslation } from 'react-i18next';

/**
 * Every identity this account bills under.
 *
 * Someone self-employed here invoices personally and through their company;
 * they are different RFCs under different régimenes, and which one a given
 * purchase belongs to is a question only the customer can answer. The default
 * is what we use when nobody says otherwise.
 */
export default function FiscalProfileList() {
  const { t } = useTranslation();
  const [profiles, setProfiles] = useState<FiscalProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<FiscalProfile | null>(null);
  const [adding, setAdding] = useState(false);

  async function load() {
    try {
      const response = await apiFetch<FiscalProfile[]>('/customer/fiscal/profiles');
      setProfiles((response.data as FiscalProfile[]) || []);
      setError(null);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  async function makeDefault(id: string) {
    setError(null);
    try {
      await apiFetch(`/customer/fiscal/profiles/${id}/default`, { method: 'POST', body: '{}' });
      await load();
    } catch (err) {
      setError((err as Error).message);
    }
  }

  async function remove(id: string) {
    setError(null);
    try {
      await apiFetch(`/customer/fiscal/profiles/${id}`, { method: 'DELETE' });
      await load();
    } catch (err) {
      // Refused when a comprobante was stamped against it — the record has to
      // keep saying who it was issued to.
      setError((err as Error).message);
    }
  }

  if (loading) {
    return (
      <div className="py-8 text-center">
        <Loader2 className="h-5 w-5 animate-spin mx-auto text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="font-semibold text-gray-900">{t('fiscal.sectionTitle')}</h3>
          <p className="text-sm text-gray-500">
            {t('fiscal.sectionHelp')}
          </p>
        </div>
        <button
          onClick={() => setAdding(true)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-brand-600 text-white rounded-lg hover:bg-brand-700 shrink-0"
        >
          <Plus className="h-4 w-4" /> {t('fiscal.add')}
        </button>
      </div>

      {error && (
        <p className="flex items-start gap-1.5 text-sm text-amber-700">
          <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
          {error}
        </p>
      )}

      {profiles.length === 0 ? (
        <p className="text-sm text-gray-500 bg-gray-50 border border-gray-200 rounded-lg p-4">
          Todavía no tienes datos fiscales guardados. Los pedimos cuando solicitas tu primera
          factura, o puedes agregarlos aquí.
        </p>
      ) : (
        <div className="space-y-3">
          {profiles.map((profile) => (
            <div key={profile.id} className="border border-gray-200 rounded-lg p-4">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-medium">{profile.label}</span>
                {profile.isDefault && (
                  <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 bg-brand-50 text-brand-700 rounded-full">
                    <Star className="h-3 w-3" /> {t('fiscal.isDefault')}
                  </span>
                )}
                <div className="flex-1" />
                {!profile.isDefault && (
                  <button
                    onClick={() => makeDefault(profile.id)}
                    className="text-sm text-brand-600 hover:underline"
                  >
                    {t('fiscal.makeDefault')}
                  </button>
                )}
                <button
                  onClick={() => setEditing(profile)}
                  className="text-sm text-gray-600 hover:underline"
                >
                  {t('fiscal.edit')}
                </button>
                <button
                  onClick={() => remove(profile.id)}
                  className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg"
                  title={t('fiscal.remove')}
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
              <dl className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm">
                <div>
                  <dt className="text-gray-500">RFC</dt>
                  <dd className="font-mono">{profile.taxId}</dd>
                </div>
                <div className="sm:col-span-2">
                  <dt className="text-gray-500">{t('fiscal.legalName')}</dt>
                  <dd>{profile.legalName}</dd>
                </div>
                <div>
                  <dt className="text-gray-500">{t('fiscal.regimeAndCp')}</dt>
                  <dd>
                    {profile.taxRegime} · {profile.fiscalPostalCode}
                  </dd>
                </div>
              </dl>
            </div>
          ))}
        </div>
      )}

      {(adding || editing) && (
        <FiscalProfileDialog
          existing={editing}
          onClose={() => {
            setAdding(false);
            setEditing(null);
          }}
          onSaved={() => {
            setAdding(false);
            setEditing(null);
            void load();
          }}
        />
      )}
    </div>
  );
}
